// @ts-check
/**
 * The kept shapes of one map as one binary blob (shipped in Storage, scripts/render_map_shapes.mjs; read back by
 * geoCache.seedGeoShapes). Layout: 'PRGEO1', a uint32 JSON length, the JSON (UTF-8) and then the typed arrays' bytes,
 * each 8-byte aligned. The JSON is the entries with every typed array replaced by {"$ta": name, "o": offset, "n": length}.
 * Plain values (numbers, strings, nested objects and arrays, null) pass through as JSON.
 */
const MAGIC = 'PRGEO1';
const TA = {
  Float32Array,
  Float64Array,
  Uint8Array,
  Uint16Array,
  Uint32Array,
  Int8Array,
  Int16Array,
  Int32Array,
};

/** @param {Map<string, any>} entries */
export function encodeGeo(entries) {
  /** @type {{ta: ArrayBufferView, off: number}[]} */
  const parts = [];
  let total = 0;
  const place = (ta) => {
    const off = Math.ceil(total / 8) * 8;
    parts.push({ ta, off });
    total = off + ta.byteLength;
    return off;
  };
  const walk = (v) => {
    if (ArrayBuffer.isView(v) && !(v instanceof DataView)) {
      const name = v.constructor.name;
      if (!TA[name]) throw new Error(`geoBlob: ${name} not supported`);
      return { $ta: name, o: place(v), n: /** @type {any} */ (v).length };
    }
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      const o = {};
      for (const [k, x] of Object.entries(v)) o[k] = walk(x);
      return o;
    }
    return v;
  };
  const json = new TextEncoder().encode(JSON.stringify([...entries].map(([k, v]) => [k, walk(v)])));
  const head = MAGIC.length + 4 + json.length;
  const base = Math.ceil(head / 8) * 8;
  const out = new Uint8Array(base + total);
  out.set(new TextEncoder().encode(MAGIC), 0);
  new DataView(out.buffer).setUint32(MAGIC.length, json.length, true);
  out.set(json, MAGIC.length + 4);
  for (const { ta, off } of parts)
    out.set(new Uint8Array(ta.buffer, ta.byteOffset, ta.byteLength), base + off);
  return out.buffer;
}

/** @param {ArrayBuffer} buf @returns {Map<string, any>} */
export function decodeGeo(buf) {
  const bytes = new Uint8Array(buf);
  const magic = new TextDecoder().decode(bytes.subarray(0, MAGIC.length));
  if (magic !== MAGIC) throw new Error('geoBlob: not a shapes blob');
  const len = new DataView(buf).getUint32(MAGIC.length, true);
  const json = JSON.parse(
    new TextDecoder().decode(bytes.subarray(MAGIC.length + 4, MAGIC.length + 4 + len)),
  );
  const base = Math.ceil((MAGIC.length + 4 + len) / 8) * 8;
  const walk = (v) => {
    if (v && typeof v === 'object' && !Array.isArray(v) && typeof v.$ta === 'string') {
      const C = TA[v.$ta];
      if (!C) throw new Error(`geoBlob: ${v.$ta} not supported`);
      // a copy, not a view: the blob is let go once read, and the arrays live on in the cache row
      return new C(buf.slice(base + v.o, base + v.o + v.n * C.BYTES_PER_ELEMENT));
    }
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      const o = {};
      for (const [k, x] of Object.entries(v)) o[k] = walk(x);
      return o;
    }
    return v;
  };
  return new Map(json.map(([k, v]) => [k, walk(v)]));
}
